import { useEffect, useState, useRef } from 'react';
import { productsApi, colorsApi, colorPresetsApi } from '@/lib/api/arrex/catalog';
import { uploadBlob } from '@/lib/upload';
import { useApp } from '@/contexts/AppContext';
import { localized, imgUrl } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, Trash2, X, ChevronDown, Package, Check, Loader2, Save, Upload, ArrowUp, ArrowDown, ArrowLeft, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { readItems } from '@/lib/api/normalize';
import { ListSkeleton } from '@/components/ui/skeletons';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface ColorDraft {
  name_en: string;
  name_ar: string;
  hex_code: string;
}

// ---------------------------------------------------------------------------
// ProductPicker
// ---------------------------------------------------------------------------
function ProductPicker({
  products, value, onChange, lang,
}: { products: any[]; value: string; onChange: (v: string) => void; lang: string }) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const current = products.find(p => p.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full flex items-center justify-between gap-2 h-12 px-4 rounded-lg border border-border bg-background/80 hover:border-primary/50 transition-all text-start"
        >
          <span className="flex items-center gap-3 min-w-0">
            <span className="text-[10px] font-mono uppercase text-muted-foreground bg-muted/60 px-1.5 py-0.5 rounded">PRD</span>
            <span className="text-sm font-medium truncate">
              {current ? localized(current, 'name', lang as any) : t('admin.colorsPage.chooseProduct')}
            </span>
          </span>
          <ChevronDown className={cn('w-4 h-4 text-muted-foreground transition-transform shrink-0', open && 'rotate-180')} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-2 w-[--radix-popover-trigger-width] max-h-80 overflow-y-auto rounded-lg" align="start">
        <div className="space-y-1">
          {products.map(p => {
            const selected = p.id === value;
            return (
              <button key={p.id} type="button"
                onClick={() => { onChange(p.id); setOpen(false); }}
                className={cn(
                  'w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-md text-start transition-colors',
                  selected ? 'bg-primary/15' : 'hover:bg-muted/60'
                )}
              >
                <span className="text-sm truncate">{localized(p, 'name', lang as any)}</span>
                {selected && <Check className="w-4 h-4 text-primary shrink-0" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function AdminProductColors() {
  const { lang } = useApp();
  const { t } = useTranslation();

  // ── catalog data ──────────────────────────────────────────────────────────
  const [products, setProducts] = useState<any[]>([]);
  const [productId, setProductId] = useState('');
  const [productSizes, setProductSizes] = useState<string[]>([]);
  const [colors, setColors] = useState<any[]>([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [colorsLoading, setColorsLoading] = useState(false);

  // ── add-color form ────────────────────────────────────────────────────────
  const [newColor, setNewColor] = useState({ name_en: '', name_ar: '', hex_code: '#000000' });
  const [saveToPresets, setSaveToPresets] = useState(false);
  const [selectedPresetId, setSelectedPresetId] = useState('');

  // ── per-color draft state (no API call until Save icon clicked) ───────────
  const [colorDrafts, setColorDrafts] = useState<Record<string, ColorDraft>>({});
  const [savingColorId, setSavingColorId] = useState<string | null>(null);

  // ── global upload lock (one color at a time) ──────────────────────────────
  const [uploadingColorId, setUploadingColorId] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState('');
  const [dragOverColorId, setDragOverColorId] = useState<string | null>(null);

  // ── misc UI state ─────────────────────────────────────────────────────────
  const [openColor, setOpenColor] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [presets, setPresets] = useState<any[]>([]);
  const [managePresetsOpen, setManagePresetsOpen] = useState(false);

  // one hidden <input type="file"> per color card
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // ── load helpers ──────────────────────────────────────────────────────────
  const loadPresets = async () => {
    try {
      const res = await colorPresetsApi.list();
      setPresets(readItems(res));
    } catch (e) {
      console.error('Failed to load presets', e);
    }
  };

  const load = async (pid: string) => {
    setColorsLoading(true);
    try {
      const prod = products.find(p => p.id === pid);
      setProductSizes(prod?.sizes || ['S', 'M', 'L', 'XL']);
      const { items } = await colorsApi.listForProduct(pid);
      const list: any[] = items || [];
      setColors(list);
      // Initialise / refresh drafts from server — only reset drafts for colors
      // that have NO unsaved local changes so we don't clobber in-progress edits.
      setColorDrafts(prev => {
        const next = { ...prev };
        list.forEach((c: any) => {
          const existing = next[c.id];
          const serverDraft: ColorDraft = { name_en: c.name_en, name_ar: c.name_ar, hex_code: c.hex };
          // Always initialise newly seen colors; leave dirty drafts untouched.
          if (!existing) next[c.id] = serverDraft;
        });
        // Remove stale draft keys for colors that were deleted
        const ids = new Set(list.map((c: any) => c.id));
        Object.keys(next).forEach(k => { if (!ids.has(k)) delete next[k]; });
        return next;
      });
    } catch (e: any) {
      console.error(e);
    } finally {
      setColorsLoading(false);
    }
  };

  useEffect(() => {
    setProductsLoading(true);
    productsApi.adminList({ pageSize: 100, sort: 'newest' })
      .then((res) => setProducts(readItems(res)))
      .finally(() => setProductsLoading(false));
    loadPresets();
  }, []);

  useEffect(() => {
    if (productId) load(productId);
    else { setColors([]); setColorDrafts({}); }
  }, [productId]); // eslint-disable-line react-hooks/exhaustive-deps

  const moveColor = async (idx: number, dir: -1 | 1) => {
    const list = [...colors];
    const target = list[idx];
    const swap = list[idx + dir];
    if (!target || !swap) return;

    const tempSort = target.sort_order;
    target.sort_order = swap.sort_order;
    swap.sort_order = tempSort;

    try {
      await colorsApi.reorder(productId, [
        { id: target.id, sort_order: target.sort_order },
        { id: swap.id, sort_order: swap.sort_order },
      ]);
      load(productId);
      toast.success('Color order updated');
    } catch (e: any) {
      toast.error(e?.message || 'Reorder failed');
    }
  };

  const moveImage = async (colorId: string, imgIdx: number, dir: -1 | 1) => {
    const color = colors.find(c => c.id === colorId);
    if (!color) return;
    const images = [...(color.images || color.product_color_images || [])];
    const target = images[imgIdx];
    const swap = images[imgIdx + dir];
    if (!target || !swap) return;

    const tempSort = target.sort_order;
    target.sort_order = swap.sort_order;
    swap.sort_order = tempSort;

    try {
      await colorsApi.reorderImages(colorId, [
        { id: target.id, sort_order: target.sort_order },
        { id: swap.id, sort_order: swap.sort_order },
      ]);
      load(productId);
      toast.success('Image order updated');
    } catch (e: any) {
      toast.error(e?.message || 'Reorder failed');
    }
  };

  // ── draft helpers ─────────────────────────────────────────────────────────
  const updateDraft = (colorId: string, patch: Partial<ColorDraft>) => {
    setColorDrafts(prev => ({
      ...prev,
      [colorId]: { ...prev[colorId], ...patch },
    }));
  };

  /** True when the local draft differs from what the server returned. */
  const isDirty = (c: any): boolean => {
    const d = colorDrafts[c.id];
    if (!d) return false;
    return d.name_en !== c.name_en || d.name_ar !== c.name_ar || d.hex_code !== c.hex;
  };

  // ── save a single color's draft ───────────────────────────────────────────
  const saveColor = async (c: any) => {
    const draft = colorDrafts[c.id];
    if (!draft) return;
    const hexRegex = /^#[0-9A-Fa-f]{3,8}$/;
    if (!hexRegex.test(draft.hex_code)) {
      return toast.error('Hex code must be a valid hex color (e.g. #FF5733)');
    }
    setSavingColorId(c.id);
    try {
      await colorsApi.update(productId, c.id, {
        name_en: draft.name_en,
        name_ar: draft.name_ar,
        hex: draft.hex_code,
      });
      toast.success('Color saved');
      // Mark this draft as clean by syncing server value
      setColorDrafts(prev => ({ ...prev, [c.id]: { ...draft } }));
      // Reload to sync the server truth into colors[]
      load(productId);
    } catch (e: any) {
      toast.error(e?.message || 'Save failed');
    } finally {
      setSavingColorId(null);
    }
  };

  // ── add color ─────────────────────────────────────────────────────────────
  const addColor = async () => {
    if (!productId || !newColor.name_en) return toast.error('Name required');
    const hexRegex = /^#[0-9A-Fa-f]{3,8}$/;
    if (!hexRegex.test(newColor.hex_code)) {
      return toast.error('Hex code must be a valid hex color code (e.g., #FFFFFF)');
    }
    try {
      if (saveToPresets) {
        const exists = presets.some(
          p => ((p.hex || p.hex_code || '').toLowerCase() === (newColor.hex_code || '').toLowerCase()) ||
               ((p.name_en || '').toLowerCase() === (newColor.name_en || '').toLowerCase())
        );
        if (!exists) {
          await colorPresetsApi.create({
            name_en: newColor.name_en,
            name_ar: newColor.name_ar || newColor.name_en,
            hex: newColor.hex_code,
          });
          loadPresets();
        }
      }
      await colorsApi.create(productId, {
        name_en: newColor.name_en,
        name_ar: newColor.name_ar || newColor.name_en,
        hex: newColor.hex_code,
      });
      setNewColor({ name_en: '', name_ar: '', hex_code: '#000000' });
      setSelectedPresetId('');
      setSaveToPresets(false);
      load(productId);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to add color');
    }
  };

  const handlePresetSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value;
    setSelectedPresetId(id);
    if (id) {
      const preset = presets.find(p => p.id === id);
      if (preset) setNewColor({ name_en: preset.name_en, name_ar: preset.name_ar || '', hex_code: preset.hex || preset.hex_code || '#000000' });
    }
  };

  const deletePreset = async (id: string) => {
    try {
      await colorPresetsApi.remove(id);
      toast.success('Preset deleted');
      loadPresets();
      if (selectedPresetId === id) setSelectedPresetId('');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to delete preset');
    }
  };

  // ── delete color ──────────────────────────────────────────────────────────
  const confirmDeleteColor = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await colorsApi.remove(productId, deleteTarget.id);
      setDeleteTarget(null);
      load(productId);
    } catch (e: any) {
      toast.error(e?.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  // ── image upload (global lock) ────────────────────────────────────────────
  const handleImageUpload = async (colorId: string, files: FileList) => {
    if (uploadingColorId) return; // safety guard
    const fileArray = Array.from(files);
    setUploadingColorId(colorId);
    let successCount = 0;
    try {
      for (let i = 0; i < fileArray.length; i++) {
        setUploadProgress(`${i + 1} / ${fileArray.length}`);
        const file = fileArray[i];
        const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
        const url = await uploadBlob('product-images', file, ext, 'colors/');
        await colorsApi.addImage(colorId, url);
        successCount++;
      }
      toast.success(`${successCount} image${successCount !== 1 ? 's' : ''} uploaded`);
      load(productId);
    } catch (e: any) {
      toast.error(e?.message || `Upload failed after ${successCount} successful upload(s)`);
    } finally {
      setUploadingColorId(null);
      setUploadProgress('');
      const ref = fileInputRefs.current[colorId];
      if (ref) ref.value = '';
    }
  };

  const removeImage = async (colorId: string, id: string) => {
    try {
      await colorsApi.removeImage(colorId, id);
      load(productId);
    } catch (e: any) {
      toast.error(e?.message || 'Delete failed');
    }
  };

  // ── stock ─────────────────────────────────────────────────────────────────
  const setStock = async (colorId: string, size: string, qty: number) => {
    const safe = Math.max(0, Math.floor(qty || 0));
    setColors(prev => prev.map(c => c.id === colorId ? { ...c, stockMap: { ...c.stockMap, [size]: safe } } : c));
    try {
      await colorsApi.setStock(colorId, size, safe);
    } catch (e: any) {
      toast.error(e?.message || 'Stock update failed');
      throw e;
    }
  };

  const bulkSetStock = async (colorId: string, qty: number) => {
    await Promise.all(productSizes.map(s => setStock(colorId, s, qty)));
    toast.success('Stock updated');
  };

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      <h1 className="font-display text-3xl font-bold">{t('admin.colorsPage.title')}</h1>

      {/* Product picker */}
      <div className="surface-card rounded-lg p-4 max-w-md space-y-2">
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">{t('admin.colorsPage.selectProduct')}</Label>
        {productsLoading ? (
          <div className="h-12 w-full flex items-center justify-center border border-border rounded-lg bg-background/50">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <ProductPicker products={products} value={productId} onChange={setProductId} lang={lang} />
        )}
      </div>

      {productId && (
        <>
          {/* ── Add Color ─────────────────────────────────────────────────── */}
          <div className="surface-card rounded-xl p-5 space-y-4 shadow-sm border border-border">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-semibold tracking-tight">{t('admin.colorsPage.addColor')}</h2>
              <Button variant="outline" size="sm" onClick={() => setManagePresetsOpen(true)}>
                Manage Saved Colors
              </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
              <div>
                <Label className="text-xs font-medium text-muted-foreground mb-1.5 block">{t('admin.colorsPage.savedPreset')}</Label>
                <select
                  value={selectedPresetId}
                  onChange={handlePresetSelect}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <option value="">-- Custom Color --</option>
                  {presets.map(p => (
                    <option key={p.id} value={p.id}>{p.name_en} ({p.hex || p.hex_code})</option>
                  ))}
                </select>
              </div>

              <div>
                <Label className="text-xs font-medium text-muted-foreground mb-1.5 block">{t('admin.common.nameEn')}</Label>
                <Input
                  className="h-10"
                  value={newColor.name_en}
                  onChange={e => setNewColor({ ...newColor, name_en: e.target.value })}
                  placeholder="e.g. Emerald"
                />
              </div>

              <div>
                <Label className="text-xs font-medium text-muted-foreground mb-1.5 block">{t('admin.common.nameAr')}</Label>
                <Input
                  className="h-10"
                  value={newColor.name_ar}
                  onChange={e => setNewColor({ ...newColor, name_ar: e.target.value })}
                  placeholder="مثال: زمردي"
                  dir="rtl"
                />
              </div>

              <div>
                <Label className="text-xs font-medium text-muted-foreground mb-1.5 block">{t('admin.colorsPage.hex')}</Label>
                <div className="flex gap-2 items-center">
                  <Input
                    type="text"
                    value={newColor.hex_code}
                    onChange={e => setNewColor({ ...newColor, hex_code: e.target.value })}
                    placeholder="#000000"
                    maxLength={9}
                    className="font-mono h-10"
                  />
                  <input
                    type="color"
                    value={/^#[0-9A-Fa-f]{6}$/.test(newColor.hex_code) ? newColor.hex_code : '#000000'}
                    onChange={e => setNewColor({ ...newColor, hex_code: e.target.value })}
                    className="w-10 h-10 p-0.5 border border-border rounded-lg cursor-pointer shrink-0 bg-transparent"
                    title={t('admin.common.pickColor')}
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-border/40 gap-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="save_to_presets"
                  checked={saveToPresets}
                  onChange={e => setSaveToPresets(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                />
                <Label htmlFor="save_to_presets" className="cursor-pointer text-xs font-medium">{t('admin.colorsPage.saveToPresets')}</Label>
              </div>
              <Button onClick={addColor} className="px-5"><Plus className="w-4 h-4 me-1.5" />{t('admin.colorsPage.addColor')}</Button>
            </div>
          </div>

          {/* ── Color List ────────────────────────────────────────────────── */}
          <div className="space-y-3">
            {colorsLoading ? (
              <ListSkeleton rows={3} />
            ) : (
              colors.map((c, idx) => {
              const isOpen = openColor === c.id;
              const summary = productSizes.map(s => `${s}:${c.stockMap?.[s] ?? 0}`).join(' • ');
              const draft = colorDrafts[c.id] ?? { name_en: c.name_en, name_ar: c.name_ar, hex_code: c.hex };
              const dirty = isDirty(c);
              const isSaving = savingColorId === c.id;
              const isUploading = uploadingColorId === c.id;
              // All other cards are locked while any upload is active
              const uploadLocked = uploadingColorId !== null && !isUploading;

              return (
                <div key={c.id} className="surface-card rounded-lg p-4">

                  {/* ── Header row: swatch · names · hex · save · delete ── */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 p-3 rounded-lg bg-muted/20 border border-border/50">

                    <div className="flex items-center gap-3 flex-1 min-w-0 flex-wrap">
                      {/* Live color swatch from draft */}
                      <div
                        className="w-9 h-9 rounded-full border-2 border-border shrink-0 shadow-sm"
                        style={{ backgroundColor: draft.hex_code }}
                      />

                      {/* Name EN */}
                      <Input
                        className="w-full sm:w-32 h-9 text-sm"
                        value={draft.name_en}
                        onChange={e => updateDraft(c.id, { name_en: e.target.value })}
                        placeholder={t('admin.common.nameEn')}
                      />

                      {/* Name AR */}
                      <Input
                        className="w-full sm:w-32 h-9 text-sm"
                        value={draft.name_ar}
                        onChange={e => updateDraft(c.id, { name_ar: e.target.value })}
                        placeholder={t('admin.common.nameAr')}
                        dir="rtl"
                      />

                      {/* Hex text input + color swatch picker */}
                      <div className="flex items-center gap-1.5">
                        <Input
                          type="text"
                          className="w-24 font-mono text-xs h-9"
                          value={draft.hex_code}
                          onChange={e => updateDraft(c.id, { hex_code: e.target.value })}
                          placeholder="#000000"
                          maxLength={9}
                        />
                        <input
                          type="color"
                          value={/^#[0-9A-Fa-f]{6}$/.test(draft.hex_code) ? draft.hex_code : '#000000'}
                          onChange={e => updateDraft(c.id, { hex_code: e.target.value })}
                          className="w-9 h-9 p-0.5 border border-border rounded cursor-pointer shrink-0 bg-transparent"
                          title={t('admin.common.pickColor')}
                        />
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center justify-end gap-1.5 shrink-0 border-t sm:border-t-0 pt-2 sm:pt-0">
                      {/* Save icon */}
                      {dirty && (
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => saveColor(c)}
                          disabled={isSaving}
                          title={t('admin.colorsPage.saveColor')}
                          className="h-9 px-3 text-xs gap-1"
                        >
                          {isSaving
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : <Save className="w-3.5 h-3.5" />}
                          Save
                        </Button>
                      )}

                      {/* Reordering Controls */}
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={() => moveColor(idx, -1)}
                        disabled={idx === 0}
                        title={t('admin.common.moveUp')}
                        className="h-9 w-9"
                      >
                        <ArrowUp className="w-4 h-4" />
                      </Button>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={() => moveColor(idx, 1)}
                        disabled={idx === colors.length - 1}
                        title={t('admin.common.moveDown')}
                        className="h-9 w-9"
                      >
                        <ArrowDown className="w-4 h-4" />
                      </Button>

                      {/* Delete */}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteTarget(c)}
                        title={t('admin.colorsPage.deleteColor')}
                        className="h-9 w-9 text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* ── Image grid ────────────────────────────────────────── */}
                  <div
                    onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragOverColorId(c.id); }}
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (dragOverColorId !== c.id) setDragOverColorId(c.id); }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                        setDragOverColorId(null);
                      }
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDragOverColorId(null);
                      if (uploadLocked) {
                        toast.error('Another upload is in progress');
                        return;
                      }
                      const files = e.dataTransfer.files;
                      if (files && files.length > 0) {
                        handleImageUpload(c.id, files);
                      }
                    }}
                    className={cn(
                      'grid grid-cols-3 md:grid-cols-6 gap-2 p-1.5 rounded-xl transition-all duration-200 relative',
                      dragOverColorId === c.id && 'ring-2 ring-primary bg-primary/5 ring-offset-2 scale-[1.01]'
                    )}
                  >
                    {/* Existing images */}
                    {(c.images || c.product_color_images || []).map((img: any, imgIdx: number) => (
                      <div key={img.id} className="flex flex-col gap-1">
                        {/* Image with hover overlay (desktop only) */}
                        <div className="relative aspect-square rounded overflow-hidden group border border-border bg-muted/20">
                          <img src={imgUrl(img.url || img.image_url)} className="w-full h-full object-cover" alt="" />

                          {/* Desktop hover overlay — hidden on mobile */}
                          <div className="absolute inset-0 bg-black/40 hidden md:flex items-center justify-center gap-1.5 opacity-0 group-hover:opacity-100 transition duration-150">
                            <button
                              type="button"
                              onClick={() => moveImage(c.id, imgIdx, -1)}
                              disabled={imgIdx === 0}
                              className="bg-background/90 hover:bg-background text-foreground rounded p-1 disabled:opacity-30 disabled:cursor-not-allowed"
                              title={t('admin.common.moveLeft')}
                            >
                              <ArrowLeft className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => moveImage(c.id, imgIdx, 1)}
                              disabled={imgIdx === (c.images || c.product_color_images || []).length - 1}
                              className="bg-background/90 hover:bg-background text-foreground rounded p-1 disabled:opacity-30 disabled:cursor-not-allowed"
                              title={t('admin.common.moveRight')}
                            >
                              <ArrowRight className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => removeImage(c.id, img.id)}
                              className="bg-destructive/90 hover:bg-destructive text-destructive-foreground rounded p-1"
                              title={t('admin.common.deleteImage')}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>

                        {/* Mobile controls strip — always visible, hidden on desktop */}
                        <div className="flex md:hidden items-center justify-center gap-1">
                          <button
                            type="button"
                            onClick={() => moveImage(c.id, imgIdx, -1)}
                            disabled={imgIdx === 0}
                            className="flex-1 flex items-center justify-center h-7 rounded border border-border bg-background hover:bg-muted text-muted-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            title={t('admin.common.moveLeft')}
                          >
                            <ArrowLeft className="w-3 h-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveImage(c.id, imgIdx, 1)}
                            disabled={imgIdx === (c.images || c.product_color_images || []).length - 1}
                            className="flex-1 flex items-center justify-center h-7 rounded border border-border bg-background hover:bg-muted text-muted-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            title={t('admin.common.moveRight')}
                          >
                            <ArrowRight className="w-3 h-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => removeImage(c.id, img.id)}
                            className="flex-1 flex items-center justify-center h-7 rounded border border-destructive/40 bg-destructive/5 hover:bg-destructive/10 text-destructive transition-colors"
                            title={t('admin.common.deleteImage')}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ))}

                    {/* Upload cell */}
                    <div
                      onClick={() => {
                        if (!uploadingColorId) fileInputRefs.current[c.id]?.click();
                      }}
                      title={uploadLocked ? 'Another color is uploading — please wait…' : 'Click or drop to upload images'}
                      className={cn(
                        'aspect-square rounded border-2 border-dashed flex flex-col items-center justify-center gap-1 transition-all select-none',
                        isUploading
                          ? 'border-primary/70 bg-primary/5 cursor-default'
                          : uploadLocked
                          ? 'border-border/30 bg-muted/10 cursor-not-allowed opacity-40'
                          : dragOverColorId === c.id
                          ? 'border-primary bg-primary/20 scale-105 shadow-md'
                          : 'border-border hover:border-primary/50 hover:bg-muted/30 cursor-pointer'
                      )}
                    >
                      {isUploading ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin text-primary" />
                          <span className="text-[11px] font-semibold text-primary leading-none">{uploadProgress}</span>
                        </>
                      ) : (
                        <>
                          <Upload className={cn('w-5 h-5', dragOverColorId === c.id ? 'text-primary animate-bounce' : uploadLocked ? 'text-muted-foreground/30' : 'text-muted-foreground')} />
                          <span className={cn('text-[10px] font-medium text-center px-1', dragOverColorId === c.id ? 'text-primary font-semibold' : uploadLocked ? 'text-muted-foreground/30' : 'text-muted-foreground')}>
                            {dragOverColorId === c.id ? 'Drop to upload' : uploadLocked ? 'Locked' : 'Upload / Drop'}
                          </span>
                        </>
                      )}
                    </div>

                    {/* Hidden file input — one per color */}
                    <input
                      ref={el => { fileInputRefs.current[c.id] = el; }}
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={e => {
                        const files = e.target.files;
                        if (files && files.length > 0) handleImageUpload(c.id, files);
                      }}
                    />
                  </div>

                  {/* ── Stock collapsible ─────────────────────────────────── */}
                  <div className="mt-4">
                    <Collapsible open={isOpen} onOpenChange={v => setOpenColor(v ? c.id : null)}>
                      <CollapsibleTrigger asChild>
                        <button
                          type="button"
                          className={cn(
                            'w-full flex items-center justify-between gap-3 h-12 px-4 rounded-lg border border-border bg-background/80 hover:border-primary/50 transition-all text-start',
                            isOpen && 'border-primary/60 bg-background'
                          )}
                        >
                          <span className="flex items-center gap-3 min-w-0 flex-1">
                            <span className="rounded-md bg-primary/10 text-primary p-1.5 shrink-0">
                              <Package className="w-4 h-4" />
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block text-sm font-semibold leading-tight">{t('admin.colorsPage.stockBySize')}</span>
                              <span className="block text-[11px] text-muted-foreground truncate">{summary}</span>
                            </span>
                          </span>
                          <ChevronDown className={cn('w-4 h-4 text-muted-foreground transition-transform shrink-0', isOpen && 'rotate-180')} />
                        </button>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="overflow-hidden data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up">
                        <div className="mt-3 space-y-3">
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            {productSizes.map(s => {
                              const qty = c.stockMap?.[s] ?? 0;
                              const tone = qty === 0
                                ? 'border-destructive/60'
                                : qty <= 3
                                ? 'border-amber-400/60'
                                : 'border-green-500/60';
                              return (
                                <div key={s} className={`rounded border-2 ${tone} p-2`}>
                                  <Label className="text-xs flex items-center justify-between">
                                    <span className="font-bold">{s}</span>
                                    <span className="text-[10px]">
                                      {qty === 0 ? '🔴 Out' : qty <= 3 ? '🟡 Low' : '🟢 OK'}
                                    </span>
                                  </Label>
                                  <Input
                                    type="number"
                                    min={0}
                                    value={qty}
                                    onChange={e => setStock(c.id, s, Number(e.target.value))}
                                    className="mt-1 h-8"
                                  />
                                </div>
                              );
                            })}
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs text-muted-foreground">Bulk set:</span>
                            <Button size="sm" variant="outline" onClick={() => bulkSetStock(c.id, 0)}>0</Button>
                            <Button size="sm" variant="outline" onClick={() => bulkSetStock(c.id, 10)}>10</Button>
                            <Button size="sm" variant="outline" onClick={() => bulkSetStock(c.id, 25)}>25</Button>
                            <Button size="sm" variant="outline" onClick={() => bulkSetStock(c.id, 50)}>50</Button>
                          </div>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  </div>
                </div>
              );
            })
            )}

            {!colorsLoading && colors.length === 0 && (
              <p className="text-muted-foreground text-sm">No colors yet. Add one above.</p>
            )}
          </div>
        </>
      )}

      {/* ── Delete Color confirmation ──────────────────────────────────────── */}
      <AlertDialog open={!!deleteTarget} onOpenChange={v => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="w-5 h-5" />
              {t('admin.deleteColor')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && t('admin.deleteColorConfirm', { name: localized(deleteTarget, 'name', lang as any) })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{t('admin.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={e => { e.preventDefault(); confirmDeleteColor(); }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : t('admin.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Manage Presets dialog ─────────────────────────────────────────── */}
      <Dialog open={managePresetsOpen} onOpenChange={setManagePresetsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('admin.colorsPage.manageSaved')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="border rounded-md overflow-hidden max-h-[300px] overflow-y-auto">
              {presets.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground text-sm">No saved colors yet.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-start p-2">{t('admin.colorsPage.nameEnAr')}</th>
                      <th className="text-start p-2">Hex</th>
                      <th className="text-end p-2">{t('admin.common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {presets.map(p => (
                      <tr key={p.id} className="border-b hover:bg-muted/30 transition-colors">
                        <td className="p-2">
                          <span className="block font-medium">{p.name_en}</span>
                          <span className="block text-xs text-muted-foreground">{p.name_ar}</span>
                        </td>
                        <td className="p-2 font-mono">
                          <span className="flex items-center gap-1.5">
                            <span className="w-4 h-4 rounded-full border border-border inline-block shrink-0" style={{ backgroundColor: p.hex || p.hex_code }} />
                            {p.hex || p.hex_code}
                          </span>
                        </td>
                        <td className="p-2 text-end">
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => deletePreset(p.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
